import type { UserSegmentIds } from '@iheartjane/data-access';
import {
  EventNames,
  coreProductProperties,
  trackError,
} from '@iheartjane/tracking';

import type { HitType } from '@jane/search/data-access';
import type { CardLocation } from '@jane/shared-ecomm/tracking';
/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct, Product, Store } from '@jane/shared/models';

interface TrackingProps {
  cardIndex?: number;
  carouselView?: boolean;
  columnPosition?: number | null;
  indexName: string;
  listView: boolean;
  menuProduct:
    HitType<(MenuProduct & { root_types: string[] }) | Product> | MenuProduct;
  menuRowName?: string;
  rowPosition?: number | null;
  userSegments?: UserSegmentIds;
}

export const buildClickTrackingEvent = ({
  store,
  menuProduct,
  carouselView,
  rowPosition,
  columnPosition,
  cardIndex,
  indexName,
  listView,
  menuRowName,
  userSegments,
}: TrackingProps & { store: Store }) => {
  if ('objectID' in menuProduct && !menuProduct?.objectID && !menuProduct?.id) {
    trackError('MenuProduct missing id', {
      menuRowName,
      menuProduct,
    });
    return undefined;
  }

  return {
    objectIds: [
      'objectID' in menuProduct
        ? menuProduct?.objectID
        : menuProduct?.id?.toString(),
    ],
    storeId: store.id.toString() || undefined,
    storeCity: store.city || undefined,
    storeState: store.state || undefined,
    bestSelling:
      'root_types' in menuProduct
        ? menuProduct.root_types?.includes('best_selling')
        : false,
    ...coreProductProperties(menuProduct, userSegments),
    ...additionalTrackingProperties({
      carouselView,
      menuProduct,
      columnPosition,
      rowPosition,
      cardIndex,
      indexName,
      listView,
      menuRowName,
    }),
  };
};

const additionalTrackingProperties = ({
  carouselView,
  menuProduct,
  columnPosition,
  rowPosition,
  cardIndex,
  indexName,
  listView,
  menuRowName,
}: TrackingProps) => {
  const sharedProperties = {
    event:
      EventNames.ClickedMenuProductCard as EventNames.ClickedMenuProductCard,
    category: menuProduct.kind,
    menuRowName,
  };

  return carouselView
    ? {
        cardIndex: undefined,
        columnPosition,
        rowPosition,
        indexName,
        cardLocation: 'grid carousel' as CardLocation,
        ...sharedProperties,
      }
    : {
        cardIndex,
        cardLocation: (listView ? 'list' : 'grid table') as CardLocation,
        rowPosition: undefined,
        columnPosition: undefined,
        indexName,
        ...sharedProperties,
      };
};
